import React from 'react';
import { Box, Flex, Text, Textarea, Button, Link, Tag, useDisclosure, VStack, Spinner, Icon, useToast, Image, Collapse, HStack, useBreakpointValue } from '@chakra-ui/react';
import { FaExternalLinkAlt, FaLightbulb, FaHistory, FaCalendarDay, FaRegClock, FaClipboardList, FaEraser } from 'react-icons/fa';
import { formatarData, formatarHora, tempoDecorrido, getColorBasedOnTime, getTextColorForBg } from '../../utils/dateUtils';
import { verificarPeriodoDia } from '../../utils/constants';
import ModalConfirmacao from './ModalConfirmacao';
import ModalDetalhesAnuncio from './ModalDetalhesAnuncio';
import { getAnuncioDetalhes, getSugestaoResposta } from '../../services/api';

const ItemPergunta = React.memo(({ pergunta, onResponder, onAbrirRespostasProntas, isSubmitting, isRemoving, onExcluir, deletingId }) => {
    const [resposta, setResposta] = React.useState('');
    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const { isOpen: isAnuncioOpen, onOpen: onAnuncioOpen, onClose: onAnuncioClose } = useDisclosure();
    const { isOpen: isHistoryOpen, onToggle: onHistoryToggle } = useDisclosure();

    const textoProntas = useBreakpointValue({ base: "Prontas", md: "Respostas Prontas" });
    const textoSugerir = useBreakpointValue({ base: "Sugerir", md: "Sugerir Resposta" });

    const [anuncio, setAnuncio] = React.useState(null);
    const [loadingAnuncio, setLoadingAnuncio] = React.useState(false);
    const [errorAnuncio, setErrorAnuncio] = React.useState(null);
    const toast = useToast();

    const [isSuggesting, setIsSuggesting] = React.useState(false);

    const tempo = tempoDecorrido(pergunta.question_created_at);
    const corDeFundo = getColorBasedOnTime(tempo);
    const corDoTexto = getTextColorForBg(corDeFundo);

    const handleConfirmarResposta = async () => { await onResponder(pergunta, resposta); onConfirmClose(); };
    const handleInserirTexto = (texto) => { setResposta(current => `${current} ${texto}`.trim()); };
    const handleVerAnuncio = async () => { onAnuncioOpen(); if (anuncio) return; setLoadingAnuncio(true); setErrorAnuncio(null); const response = await getAnuncioDetalhes(pergunta.ml_item_id, pergunta.account_id); if (response.success) { setAnuncio(response.data); } else { setErrorAnuncio(response.message); } setLoadingAnuncio(false); };

    const handleSuggestAnswer = async () => {
        if (!pergunta.text) return;

        setIsSuggesting(true);

        const response = await getSugestaoResposta(pergunta.text, pergunta.ml_item_id);

        if (response.success && response.data.suggested_answer) {
            setResposta(`${response.data.suggested_answer}`);
            toast({
                title: "Sugestão encontrada!",
                description: "Atenção: A sugestão de resposta pode cometer erros. Sempre revise antes de enviar.",
                status: "info",
                duration: 3000,
                isClosable: true,
                position: "top",
            });
        } else {
            toast({
                title: "Nenhuma sugestão encontrada",
                description: "Não encontramos uma resposta.",
                status: "info",
                duration: 3000,
                isClosable: true,
                position: "top",
            });
        }

        setIsSuggesting(false);
    };

    return (
        <>
            <Box
                position="relative"
                transition="opacity 0.3s ease-out, transform 0.3s ease-out"
                opacity={isRemoving ? 0 : 1}
                transform={isRemoving ? 'scale(0.95)' : 'scale(1)'}
                pointerEvents={isRemoving ? 'none' : 'auto'}
            >
                <VStack
                    boxShadow='lg'
                    borderRadius="lg"
                    bg="#FF9F59"
                    p={{ base: 3, md: 4 }}
                    spacing={4}
                    align="stretch"
                >
                    <VStack spacing={3} align="stretch">
                        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                            <Flex
                                w={{ base: "100%", md: "auto" }}
                                justify={{ base: "space-between", md: "flex-start" }}
                                align="center"
                                gap={{ md: 2 }}
                            >
                                <Tag fontSize={{ base: "10px", md: "14px" }} colorScheme={pergunta.status === 'UNANSWERED' ? 'yellow' : 'green'}>{pergunta.status === 'UNANSWERED' ? 'A Responder' : 'Respondida'}</Tag>
                                <Tag fontSize={{ base: "10px", md: "14px" }} colorScheme={pergunta.account_id === 1 ? 'yellow' : pergunta.account_id === 3 ? 'green' : 'blue'}>Conta {pergunta.account_id}</Tag>
                                <Link fontSize={{ base: "10px", md: "14px" }} href={`https://produto.mercadolivre.com.br/MLB-${pergunta.ml_item_id.substring(3)}`} isExternal display="flex" alignItems="center" gap={1} fontWeight="bold" _hover={{ textDecoration: 'underline' }}>{pergunta.ml_item_id} <Icon as={FaExternalLinkAlt} mx={1} /></Link>
                            </Flex>

                            <HStack display={{ base: 'none', md: 'flex' }} spacing={4} align="center">
                                {pergunta.status === 'UNANSWERED' && (
                                    <Tag size="md" bg={corDeFundo} color={corDoTexto} variant="solid" h="auto" textAlign="center" py={1.5} px={2.5}>
                                        <HStack spacing={1.5}>
                                            <Icon as={FaRegClock} />
                                            <Text fontSize="12px" fontWeight="bold">{tempo}</Text>
                                        </HStack>
                                    </Tag>
                                )}
                                <HStack p={1.5} bg="white" borderRadius="md" spacing={3} divider={<Box borderLeft="1px solid" borderColor="gray.200" h="15px" />}>
                                    <HStack spacing={1.5}>
                                        <Icon as={FaCalendarDay} color="gray.600" />
                                        <Text fontSize="11px" whiteSpace="nowrap" fontWeight="bold">{formatarData(pergunta.question_created_at)}</Text>
                                    </HStack>
                                    <HStack spacing={1.5}>
                                        <Icon as={FaRegClock} color="gray.600" />
                                        <Text fontSize="11px" whiteSpace="nowrap" color="gray.600">{formatarHora(pergunta.question_created_at)}</Text>
                                    </HStack>
                                </HStack>
                            </HStack>
                        </Flex>

                        <Flex
                            display={{ base: 'flex', md: 'none' }}
                            justify="space-between"
                            align="center"
                        >
                            <VStack spacing={1} align="center">
                                <Image src={pergunta.thumbnail_url} alt={`Ver detalhes do anúncio ${pergunta.ml_item_id}`} boxSize="50px" objectFit="contain" borderRadius="md" bg="white" onClick={handleVerAnuncio} cursor="pointer" _hover={{ transform: 'scale(1.05)', shadow: 'md' }} transition="all 0.2s ease-in-out" />
                                <Text fontSize="xs" fontStyle="italic" color="gray.600">Ver Anúncio</Text>
                            </VStack>
                            <VStack spacing={0.5} p={1.5} bg="white" borderRadius="md">
                                <HStack spacing={1.5}>
                                    <Icon as={FaCalendarDay} color="gray.600" boxSize="12px" />
                                    <Text fontSize="11px" whiteSpace="nowrap" fontWeight="bold">{formatarData(pergunta.question_created_at)}</Text>
                                </HStack>
                                <HStack spacing={1.5}>
                                    <Icon as={FaRegClock} color="gray.600" boxSize="12px" />
                                    <Text fontSize="11px" whiteSpace="nowrap" color="gray.600">{formatarHora(pergunta.question_created_at)}</Text>
                                </HStack>
                            </VStack>
                            {pergunta.status === 'UNANSWERED' && (
                                <Tag fontSize="10px" bg={corDeFundo} color={corDoTexto} variant="solid" maxWidth="90px" whiteSpace="normal" h="auto" textAlign="center" p={1.5}>
                                    <VStack spacing={1}>
                                        <Icon as={FaRegClock} boxSize="12px" />
                                        <Text lineHeight="1.1" fontWeight="bold">{tempo}</Text>
                                    </VStack>
                                </Tag>
                            )}
                        </Flex>
                    </VStack>

                    <VStack spacing={2} align="stretch">
                        <Flex gap={2} mb={0} alignItems="flex-start">
                            <VStack
                                spacing={1}
                                align="center"
                                display={{ base: 'none', md: 'flex' }}
                                flexShrink={0}
                            >
                                <Image
                                    src={pergunta.thumbnail_url}
                                    alt={`Ver detalhes do anúncio ${pergunta.ml_item_id}`}
                                    boxSize="60px"
                                    objectFit="contain"
                                    borderRadius="md"
                                    bg="white"
                                    onClick={handleVerAnuncio}
                                    cursor="pointer"
                                    _hover={{ transform: 'scale(1.05)', shadow: 'md' }}
                                    transition="all 0.2s ease-in-out"
                                />
                                <Text fontSize="10px" fontStyle="italic" color="gray.600">
                                    Ver Anúncio
                                </Text>
                            </VStack>
                            <Box bg="#faddc8" p={2} borderRadius="md" flex="1" minWidth="0">
                                <Text fontSize={{ base: "12px", md: "14px" }} wordBreak="break-word" whiteSpace="pre-wrap">
                                    <Text as="span" fontWeight="bold">{pergunta.customer_nickname}:</Text> {pergunta.text}
                                </Text>
                            </Box>
                        </Flex>

                        {pergunta.status === 'UNANSWERED' ? (
                            <VStack spacing={2} align="stretch">
                                <Textarea
                                    value={resposta}
                                    onChange={(e) => setResposta(e.target.value)}
                                    placeholder="Digite sua resposta..."
                                    bg="white"
                                    _placeholder={{ color: 'gray.500' }}
                                    focusBorderColor="orange.400"
                                    rows={3}
                                />
                                <Flex
                                    direction={{ base: 'column-reverse', md: 'row' }}
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={2}
                                >
                                    {/* [NOVO] HStack com 3 botões */}
                                    <HStack spacing={2} w={{ base: '100%', md: 'auto' }}>
                                        <Button
                                            size="sm"
                                            onClick={() => onAbrirRespostasProntas(handleInserirTexto)}
                                            leftIcon={<FaClipboardList />}
                                            flex={1}
                                        >
                                            <Text fontSize={{ base: '12px', md: '12px' }}>
                                                {textoProntas}
                                            </Text>
                                        </Button>
                                        <Button
                                            size="sm"
                                            colorScheme="purple"
                                            onClick={handleSuggestAnswer}
                                            leftIcon={<FaLightbulb />}
                                            isLoading={isSuggesting}
                                            flex={1}
                                        >
                                            <Text fontSize={{ base: '12px', md: '12px' }}>
                                                {textoSugerir}
                                            </Text>
                                        </Button>
                                        <Button
                                            size="sm"
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={() => setResposta('')}
                                            leftIcon={<FaEraser />}
                                            isDisabled={!resposta.trim()}
                                            flex={1}
                                        >
                                            <Text fontSize={{ base: '12px', md: '12px' }}>
                                                Limpar
                                            </Text>
                                        </Button>
                                    </HStack>

                                    <HStack spacing={2} w={{ base: '100%', md: 'auto' }}>
                                        <Button
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={() => onExcluir(pergunta.id, pergunta.account_id)}
                                            isLoading={deletingId === pergunta.id}
                                            flex={1}
                                        >
                                            Excluir
                                        </Button>
                                        <Button
                                            colorScheme="teal"
                                            onClick={onConfirmOpen}
                                            isDisabled={!resposta.trim()}
                                            flex={1}
                                        >
                                            Responder
                                        </Button>
                                    </HStack>
                                </Flex>
                            </VStack>
                        ) : (
                            <Box bg="gray.100" p={3} borderRadius="md">
                                <Text whiteSpace="pre-wrap"><b>Sua Resposta:</b> {pergunta.answer_text}</Text>
                            </Box>
                        )}
                    </VStack>

                    {pergunta.resposta_anteriores && pergunta.resposta_anteriores.length > 0 && (
                        <Box pt={1}>
                            <Button
                                width="100%"
                                size="sm"
                                variant="outline"
                                colorScheme="orange"
                                bg="orange.50"
                                _hover={{ bg: 'orange.100' }}
                                onClick={onHistoryToggle}
                                leftIcon={<FaHistory />}
                            >
                                {isHistoryOpen ? 'Fechar Histórico' : `Ver Histórico (${pergunta.resposta_anteriores.length})`}
                            </Button>
                            <Collapse in={isHistoryOpen} animateOpacity>
                                <VStack spacing={2} align="stretch" mt={0} p={3} bg="#ffead9" borderRadius="md">
                                    {pergunta.resposta_anteriores.map((hist) => (
                                        <VStack key={hist.id} align="stretch" spacing={2}>
                                            <Box bg="#faddc8" p={2} borderRadius="md">
                                                <Text fontSize="sm" wordBreak="break-word" whiteSpace="pre-wrap">
                                                    <Text as="span" fontWeight="bold">{hist.customer_nickname}:</Text> {hist.text}
                                                </Text>
                                            </Box>
                                            {hist.answer_text && (
                                                <Box bg="gray.100" p={2} borderRadius="md">
                                                    <Text fontSize="sm" whiteSpace="pre-wrap">
                                                        <b>Sua Resposta:</b> {hist.answer_text}
                                                    </Text>
                                                </Box>
                                            )}
                                        </VStack>
                                    ))}
                                </VStack>
                            </Collapse>
                        </Box>
                    )}
                </VStack>

                {isSubmitting && (
                    <Flex
                        position="absolute"
                        top="0" left="0" right="0" bottom="0"
                        bg="whiteAlpha.700"
                        borderRadius="lg"
                        zIndex="10"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Spinner size="xl" color="orange.500" thickness="4px" />
                    </Flex>
                )}
            </Box>

            <ModalConfirmacao isOpen={isConfirmOpen} onClose={onConfirmClose} onConfirm={handleConfirmarResposta} pergunta={pergunta} resposta={resposta} isLoading={isSubmitting} />
            <ModalDetalhesAnuncio isOpen={isAnuncioOpen} onClose={onAnuncioClose} anuncio={anuncio} isLoading={loadingAnuncio} error={errorAnuncio} />
        </>
    );
});

export default ItemPergunta;